// The Scenes tab — Flutter's scenesScreen.dart, plus the room's actual scenes.
//
// The Dart is a pure Location -> Room picker: you tap a room to reach its
// scenes. That is kept, but each room now also shows its scenes as chips you can
// tap to run, so every available scene is reachable from this one screen — the
// same set the desktop Scenes page lists (both read core/scenes/allScenes).
//
// Running is open to anyone with access to the room. Adding/editing stays on the
// room's scene page and stays owner-only.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, CircularProgress, Accordion, AccordionSummary,
  AccordionDetails, List, ListItemButton, ListItemIcon, ListItemText, Snackbar,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate } from 'react-router-dom';
import { CenterTitleBar } from '../../components/MobileAppBar';
import { useApp } from '../../../../core/state/AppContext';
import { fetchScenesTree, countScenes } from '../../../../core/scenes/allScenes';
import { runScene } from '../../../desktop/pages/scenes/scenesApi';

export default function ScenesTab() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { userId, locations } = useApp();

  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningId, setRunningId] = useState(null);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      setTree(await fetchScenesTree(userId, locations).catch(() => []));
    } finally {
      setLoading(false);
    }
  }, [userId, locations]);

  useEffect(() => { load(); }, [load]);

  const run = async (e, scene) => {
    e.stopPropagation();
    if (runningId) return;
    setRunningId(scene.id);
    setToast(`Running "${scene.name}"…`);
    try {
      const r = await runScene(userId, scene);
      // A server refusal (403 / 404 / 409) is the answer, not a failure to retry.
      setToast(r && r.ok === false ? `"${scene.name}": ${r.error || 'not run'}` : `Ran "${scene.name}"`);
    } finally {
      setRunningId(null);
    }
  };

  const hasRooms = tree.some((t) => t.rooms.length > 0);
  const total = countScenes(tree);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: c.back }}>
      <CenterTitleBar title="Scenes" />
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress sx={{ color: c.text }} />
          </Box>
        ) : !hasRooms ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <Typography sx={{ color: c.text, fontSize: 22 }}>No rooms available</Typography>
          </Box>
        ) : (
          <>
            {total === 0 && (
              <Typography sx={{ color: `${c.text}B3`, px: 2, pb: 1, fontSize: 14 }}>
                No scenes yet — open a room to see its devices.
              </Typography>
            )}
            {tree.filter((t) => t.rooms.length > 0).map((t) => (
              <Accordion
                key={t.location.LocationID}
                defaultExpanded
                disableGutters
                elevation={0}
                sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: c.text }} />}>
                  <Typography sx={{ color: c.text, fontWeight: 700, fontSize: 16 }}>
                    {t.location.LocationName || 'Location'}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <List disablePadding>
                    {t.rooms.map(({ room, scenes }) => (
                      <Box key={room.RoomID}>
                        <ListItemButton
                          onClick={() => navigate(`/room/${room.RoomID}/scenes`, {
                            // locationId travels with the room: scenes are stored
                            // under that location's owner, and this tab spans
                            // every location, so the one selected in context is
                            // not necessarily this room's.
                            state: {
                              roomName: room.RoomName,
                              locationId: t.location.LocationID,
                            },
                          })}
                        >
                          <ListItemIcon sx={{ color: c.text, minWidth: 40 }}>
                            <MeetingRoomIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={room.RoomName || 'Room'}
                            secondary={scenes.length
                              ? `${scenes.length} scene${scenes.length === 1 ? '' : 's'}`
                              : 'No scenes'}
                            primaryTypographyProps={{ sx: { color: c.text } }}
                            secondaryTypographyProps={{ sx: { color: `${c.text}99`, fontSize: 12 } }}
                          />
                          <ChevronRightIcon sx={{ color: `${c.text}B3` }} />
                        </ListItemButton>

                        {scenes.length > 0 && (
                          <Box sx={{
                            display: 'flex', flexWrap: 'wrap', gap: 1, px: 2, pb: 1.5,
                          }}
                          >
                            {scenes.map((s) => (
                              <Box
                                key={s.id}
                                onClick={(e) => run(e, s)}
                                sx={{
                                  bgcolor: s.color,
                                  color: s.textColor,
                                  borderRadius: 2,
                                  px: 1.5,
                                  py: 0.75,
                                  minWidth: 92,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: 0.25,
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                  opacity: runningId && runningId !== s.id ? 0.5 : 1,
                                }}
                              >
                                {runningId === s.id
                                  ? <CircularProgress size={18} sx={{ color: s.textColor }} />
                                  : <AutoAwesomeIcon fontSize="small" />}
                                <Typography variant="caption" fontWeight={700} noWrap sx={{ maxWidth: 120 }}>
                                  {s.name}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </>
        )}
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
